<template>
  <div class="flex justify-content-between flex-wrap mb-3">
    <div class="flex align-items-center justify-content-center">
      <h3>
        {{ t('navbar.myData') }}
      </h3>
    </div>
    <div class="flex flex-row-reverse">
      <div class="mr-2">
        <Button
          class="p-button p-button-primary"
          placeholder="bottom"
          @click="toggleCustomSchemaModal"
        >
          {{ t('myShopData.button.addCustomSchema') }}
          <Badge
            class="p-button-with-badge"
            value="new"
          />
        </Button>
        <CustomSchemaEditorModal
          v-if="isCustomObjectModalOpen"
          :id-custom-data="currentCustomDataId !== null ? currentCustomDataId : undefined"
          @close="toggleCustomSchemaModal"
        />
      </div>
    </div>
  </div>
  <div class="grid">
    <div class="col-12 col-fixed carriers">
      <ConfirmPopup group="basicGroup" />
      <SpmTable
        :key="tableDataRefKey"
        v-model="tableData"
        name=""
        index="type"
        :table-columns="columns"
      >
        <template #schema_type="columnProps">
          {{ t(`myShopData.schemaType.${columnProps.data.schema_type}`) }}
        </template>
        <template #schema_name="columnProps">
          <div class="flex flex-column">
            <span v-if="columnProps.data.schema_type === 'system'">{{ t(`myShopData.syncs.${columnProps.data.schema_name}`) }}</span>
            <span v-else>{{ columnProps.data.schema_name }}</span>
            <span
              v-if="columnProps.data.last_date_sync"
              class="text-xs text-500"
            >
              {{ t('myShopData.syncs.lastDateSync') }}: {{ formatMysqlDateToLocale(locale.value, columnProps.data.last_date_sync) }}
            </span>
          </div>
        </template>
        <!-- New template for status -->
        <template #status="columnProps">
          <div
            v-if="columnProps.data.schema_type === 'system'"
            class="flex align-items-center status active"
          >
            <span class="badge-dot mr-2" />
            <span>{{ t('myShopData.status.active') }}</span>
          </div>
          <div
            v-else-if="typeof columnProps.data.active === 'boolean'"
            class="flex align-items-center status"
            :class="columnProps.data.active ? 'active' : 'inactive'"
          >
            <span class="badge-dot mr-2" />
            <span>{{ t(`myShopData.status.${columnProps.data.active ? 'active' : 'inactive'}`) }}</span>
          </div>
        </template>
      </SpmTable>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onBeforeMount,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import SpmTable from '@/components/table/SpmTable.vue';
import {
  SpmTableAction, SpmTableColumns, SpmTableState, SpmTableFilterOption,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
import { UserState } from '@/composables/User';
import { formatMysqlDateToLocale } from '@/helpers/Date';
import Badge from 'primevue/badge';
import Tooltip from 'primevue/tooltip';
import Button from 'primevue/button';
import CustomSchemaEditorModal from '@/components/custom-data/CustomSchemaEditorModal.vue';
import { FilterOperatorsEnum } from '@/types/enums';
import {
  fetchAllSchemasData, SchemaType, SchemaItem, activateCustomSchema, deactivateCustomSchema, deleteCustomSchema,
} from '@/composables/custom-data/customData';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmPopup from 'primevue/confirmpopup';

export default defineComponent({
  name: 'SyncList',
  components: {
    SpmTable,
    Badge,
    Button,
    CustomSchemaEditorModal,
    ConfirmPopup,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const confirm = useConfirm();
    const { t, locale } = useI18n();
    // We are only interested with "selectedItems" here, unless "items" are to be transformed ...
    const tableData = ref<SpmTableState>({
      items: [],
      total: 1,
      error: '',
      selectedItems: [],
      isLoading: true,
    });

    const isCustomObjectModalOpen = ref(false);
    const currentCustomDataId = ref<number|null>(null);
    const tableDataRefKey = ref(0);
    const actions: SpmTableAction[] = [
      {
        icon: 'far fa-eye',
        label: t('myShopData.syncs.viewData'),
        show: () => true,
        callback: (sync: SchemaItem) => {
          const routeConfig: Record<string, any> = {
            name: `shop.syncs.${sync.type}`,
          };

          if (sync.schema_type === SchemaType.Custom && sync.id_custom_object) {
            routeConfig.query = { id: sync.id_custom_object };
          }
          // For system types, ensure navigation is correctly handled if 'shop.syncs.[system_schema_name]' exists
          // or adjust routeConfig.name accordingly if system schemas have a different naming pattern for routes.
          router.push(routeConfig);
        },
      },
      {
        icon: 'fas fa-pencil-alt',
        label: t('customData.syncList.editObject'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom,
        callback: (sync: SchemaItem) => {
          currentCustomDataId.value = sync.id_custom_object ? Number(sync.id_custom_object) : null;
          isCustomObjectModalOpen.value = true;
        },
      },
      {
        icon: 'fas fa-play-circle',
        label: t('myShopData.actions.activate'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom && sync.active === false,
        callback: (sync: SchemaItem) => {
          // Call API to activate the custom schema
          if (!sync.id_custom_object) return;
          activateCustomSchema(sync.id_custom_object).then(async (success) => {
            if (success) {
              // Refresh table data after activation
              tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
              tableDataRefKey.value += 1;
            }
          });
        },
      },
      {
        icon: 'fas fa-pause-circle',
        label: t('myShopData.actions.deactivate'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom && sync.active === true,
        callback: (sync: SchemaItem) => {
          // Call API to deactivate the custom schema
          if (!sync.id_custom_object) return;
          deactivateCustomSchema(sync.id_custom_object).then(async (success) => {
            if (success) {
              // Refresh table data after deactivation
              tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
              tableDataRefKey.value += 1;
            }
          });
        },
      },
      {
        icon: 'far fa-trash-alt',
        label: t('myShopData.actions.delete'),
        show: (sync: SchemaItem) => sync.schema_type === SchemaType.Custom,
        callback: (sync: SchemaItem, event: Event) => {
          if (!sync.id_custom_object) return;
          confirm.require({
            group: 'basicGroup',
            target: event?.currentTarget instanceof HTMLElement ? event.currentTarget : undefined,
            message: t('customData.syncList.confirmDelete'),
            header: t('confirmation'),
            icon: 'far fa-exclamation-triangle',
            acceptLabel: t('yes'),
            rejectLabel: t('no'),
            acceptClass: 'p-button-danger',
            rejectClass: 'p-button-secondary',
            accept: async () => {
              const id = sync.id_custom_object as number;
              const success = await deleteCustomSchema(id);
              if (success) {
                tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
                tableDataRefKey.value += 1;
              }
            },
          });
        },
      },
    ];

    const schemaTypeOptions: SpmTableFilterOption[] = [
      {
        label: t('myShopData.schemaType.system'),
        value: SchemaType.System,
      },
      {
        label: t('myShopData.schemaType.custom'),
        value: SchemaType.Custom,
      },
    ];

    const columns: SpmTableColumns[] = [
      {
        field: 'type',
        header: '',
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'schema_name',
        header: t('myShopData.syncs.schema_name'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'schema_type',
        header: t('myShopData.syncs.schema_type'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        filterSettings: {
          type: 'multiSelect',
          options: schemaTypeOptions,
          hideFilterMenu: true,
          showFilterInput: false,
          defaultMatchMode: FilterOperatorsEnum.IN,
        },
      },
      {
        field: 'status',
        header: t('myShopData.syncs.status'),
        sortable: false,
        filterable: true,
        editable: false,
        style: 'width:10%',
        type: 'text',
      },
      {
        field: '',
        header: t('templates.actions'),
        sortable: false,
        filterable: false,
        editable: false,
        style: 'width:10%',
        type: 'action',
        actions,
      },
    ];

    onBeforeMount(async () => {
      tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
      tableData.value.isLoading = false;
    });

    const toggleCustomSchemaModal = async () => {
      if (isCustomObjectModalOpen.value) {
        tableData.value.items = await fetchAllSchemasData(UserState.activeShop?.id ?? 0);
        tableDataRefKey.value += 1;
      }
      isCustomObjectModalOpen.value = !isCustomObjectModalOpen.value;
      if (!isCustomObjectModalOpen.value) currentCustomDataId.value = null;
    };

    return {
      t,
      columns,
      tableData,
      isCustomObjectModalOpen,
      toggleCustomSchemaModal,
      currentCustomDataId,
      locale,
      formatMysqlDateToLocale,
      tableDataRefKey,
    };
  },
});
</script>

<style lang="scss" scoped>
.status {
  .badge-dot {
    width: 8px; // Standard dot size
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }

  &.active {
    color: #10b981; // Green for active

    .badge-dot {
      background-color: #10b981;
    }
  }

  &.inactive {
    color: #db5858; // Red for inactive/disabled

    .badge-dot {
      background-color: #db5858;
    }
  }
}

.p-button-with-badge {
  color: $brand-color-primary !important;
}
</style>
